/**
 * Northern Ireland Schema Transformer
 * 
 * Transforms budget schema labels for Northern Ireland users.
 * 
 * Changes applied:
 * 1. Council Tax → Council Rates
 * 2. Water supply/waste → HIDE if no existing data (NI users don't pay water charges)
 *    - If existing data present (legacy/creditor), show with "(not applicable to Northern Ireland)"
 * 3. Savings → appends "Please include any Child Trust Fund contributions here."
 * 4. Utilities section → removes "Water" from section helper text, help dialog description, and FAQs
 */

// Type for FAQ items in helpDialog
interface FAQ {
  question: string;
  answer: string;
}

// Type for helpDialog
interface HelpDialog {
  title?: string;
  description?: string;
  faqs?: FAQ[];
}

// Type for field configuration (minimal subset we need)
interface FieldConfig {
  fieldName?: string;
  label?: string | null;
  helpDialog?: HelpDialog;
  calculatedIncomeHelpDialog?: HelpDialog;
  children?: FieldConfig[];
  ui?: {
    placeholder?: string;
    [key: string]: any;
  };
}

// Type for section configuration
interface SectionConfig {
  sectionName?: string;
  sectionLabel?: string;
  sectionHelperText?: string;
  helpDialog?: HelpDialog;
  fields?: FieldConfig[];
}

// Type for schema (minimal subset we need)
interface SchemaConfig {
  sections?: SectionConfig[];
}

/**
 * Options for NI schema transformation
 */
export interface NITransformOptions {
  /** Existing expense data from API to check for water data */
  expenseData?: {
    waterBill?: number | null | undefined;
    waterWaste?: number | null | undefined;
  } | undefined;
}

/**
 * Check if water supply (waterBill) data exists
 */
function hasExistingWaterBillData(expenseData: NITransformOptions['expenseData']): boolean {
  if (!expenseData) return false;
  const waterBill = expenseData.waterBill;
  return typeof waterBill === 'number' && waterBill > 0;
}

/**
 * Check if water waste data exists
 */
function hasExistingWaterWasteData(expenseData: NITransformOptions['expenseData']): boolean {
  if (!expenseData) return false;
  const waterWaste = expenseData.waterWaste;
  return typeof waterWaste === 'number' && waterWaste > 0;
}

/**
 * NI Label Transformation Configuration
 */
interface NILabelTransform {
  fieldName: string;
  labelTransform?: (original: string) => string;
  helpDialogTransform?: (helpDialog: HelpDialog) => HelpDialog;
  calculatedIncomeHelpDialogTransform?: (helpDialog: HelpDialog) => HelpDialog;
}

/**
 * Replace "council tax" with "council rates" in text (case-insensitive for matching, preserves case in output)
 * Also handles grammar: "council tax is" → "council rates are" (rates is plural)
 * 
 * Exported for use in other NI transformations (e.g., Quick check warnings)
 */
export function replaceCouncilTaxWithRates(text: string): string {
  // First, handle "council tax is" → "council rates are" (grammar fix: rates is plural)
  let result = text
    .replace(/Council Tax is/g, 'Council Rates are')
    .replace(/council tax is/g, 'council rates are')
    .replace(/Council tax is/g, 'Council rates are')
    .replace(/COUNCIL TAX IS/g, 'COUNCIL RATES ARE');
  
  // Then handle remaining "council tax" → "council rates" (without "is")
  result = result
    .replace(/Council Tax/g, 'Council Rates')
    .replace(/council tax/g, 'council rates')
    .replace(/Council tax/g, 'Council rates')
    .replace(/COUNCIL TAX/g, 'COUNCIL RATES');
  
  return result;
}

/**
 * Transform council tax helpDialog for NI users
 * Only transforms FAQ content, not title/description (which already say "Council tax or rates")
 */
function transformCouncilTaxHelpDialog(helpDialog: HelpDialog): HelpDialog {
  const transformed: HelpDialog = { ...helpDialog };
  
  // NOTE: Title and description intentionally NOT transformed
  // They already say "Council tax or rates" which covers both regions
  
  // Transform FAQs only
  if (transformed.faqs && Array.isArray(transformed.faqs)) {
    transformed.faqs = transformed.faqs.map((faq) => ({
      question: replaceCouncilTaxWithRates(faq.question),
      answer: replaceCouncilTaxWithRates(faq.answer),
    }));
  }
  
  return transformed;
}

/**
 * Transform calculated income helpDialog for council rates (NI)
 * Only transforms FAQ content, not description
 */
function transformCouncilTaxCalcHelpDialog(helpDialog: HelpDialog): HelpDialog {
  const transformed: HelpDialog = { ...helpDialog };
  
  // NOTE: Description intentionally NOT transformed - it mentions "council tax" generically
  
  // Transform FAQs only
  if (transformed.faqs && Array.isArray(transformed.faqs)) {
    transformed.faqs = transformed.faqs.map((faq) => ({
      question: replaceCouncilTaxWithRates(faq.question),
      answer: replaceCouncilTaxWithRates(faq.answer),
    }));
  }
  
  return transformed;
}

/**
 * Remove "Water" mentions from utilities section helper text for NI users
 * Handles various patterns:
 * - "Gas, Electricity, Water, Internet" → "Gas, Electricity, Internet"
 * - "Gas, Electricity, Water and Internet" → "Gas, Electricity and Internet"
 */
export function removeWaterFromText(text: string): string {
  let result = text;
  
  // Pattern 1: "Water, " (Water followed by comma and space) - most common in lists
  result = result.replace(/Water,\s*/gi, '');
  
  // Pattern 2: ", Water" at the end or before "and" (e.g., "Gas, Electricity, Water and Phone")
  result = result.replace(/,\s*Water(?=\s+and\s)/gi, '');
  
  // Pattern 3: "water and " in prose (e.g., "gas, electricity, water and other")
  // But preserve "energy and" when water is removed
  result = result.replace(/,?\s*water\s+and\s+/gi, ' and ');
  
  // Pattern 4: " and water" at the end of a list
  result = result.replace(/\s+and\s+water(?=[^a-zA-Z]|$)/gi, '');
  
  // Pattern 5: ", water," in the middle of a list
  result = result.replace(/,\s*water,/gi, ',');
  
  // Pattern 6: "or water" (e.g., "energy or water company")
  result = result.replace(/\s+or\s+water/gi, '');
  
  // Clean up any double spaces
  result = result.replace(/\s{2,}/g, ' ');
  
  // Clean up any double commas or comma-space-comma
  result = result.replace(/,\s*,/g, ',');
  
  return result;
}

/**
 * Check if a section is the utilities section
 */
function isUtilitiesSection(section: SectionConfig): boolean {
  return section.sectionName === 'utilitiesSection';
}

/**
 * Check if a section is the household section
 */
function isHouseholdSection(section: SectionConfig): boolean {
  return section.sectionName === 'householdSection';
}

/**
 * NI label transformation configurations (excludes water - handled separately)
 */
const NI_TRANSFORMS: NILabelTransform[] = [
  {
    fieldName: 'councilTax',
    labelTransform: () => 'How much do you pay for your Council Rates?',
    helpDialogTransform: transformCouncilTaxHelpDialog,
    calculatedIncomeHelpDialogTransform: transformCouncilTaxCalcHelpDialog,
  },
  {
    fieldName: 'savingsAmount',
    labelTransform: (original) =>
      original.includes('Child Trust Fund')
        ? original
        : `${original} Please include any Child Trust Fund contributions here.`,
  },
];

/**
 * Water field transforms - only applied when water data exists for NI users
 */
const NI_WATER_TRANSFORMS: NILabelTransform[] = [
  {
    fieldName: 'water',
    labelTransform: (original) =>
      original.includes('(not applicable to Northern Ireland)')
        ? original
        : `${original} (not applicable to Northern Ireland)`,
  },
  {
    fieldName: 'waterWaste',
    labelTransform: (original) =>
      original.includes('(not applicable to Northern Ireland)')
        ? original
        : `${original} (not applicable to Northern Ireland)`,
  },
];

/**
 * Recursively visit and transform fields in schema
 */
function visitAndTransformFields(
  fields: FieldConfig[] | undefined,
  transforms: NILabelTransform[]
): void {
  if (!fields || !Array.isArray(fields)) {
    return;
  }

  for (const field of fields) {
    const transform = transforms.find((t) => t.fieldName === field.fieldName);

    if (transform) {
      // Transform label
      if (transform.labelTransform && typeof field.label === 'string') {
        field.label = transform.labelTransform(field.label);
      }

      // Transform helpDialog
      if (transform.helpDialogTransform && field.helpDialog) {
        field.helpDialog = transform.helpDialogTransform(field.helpDialog);
      }

      // Transform calculatedIncomeHelpDialog
      if (
        transform.calculatedIncomeHelpDialogTransform &&
        field.calculatedIncomeHelpDialog
      ) {
        field.calculatedIncomeHelpDialog =
          transform.calculatedIncomeHelpDialogTransform(
            field.calculatedIncomeHelpDialog
          );
      }
    }

    // Transform ui.placeholder for any field that contains "council tax" text
    // This handles cases like councilTax.notes placeholder
    if (field.ui && typeof field.ui.placeholder === 'string') {
      field.ui.placeholder = replaceCouncilTaxWithRates(field.ui.placeholder);
    }

    // Recurse into children
    if (field.children && Array.isArray(field.children)) {
      visitAndTransformFields(field.children, transforms);
    }
  }
}

/**
 * Apply Northern Ireland schema transformations
 * 
 * This function mutates the schema in place for performance.
 * Ensure you're working with a cloned schema before calling this.
 * 
 * Water fields behavior for NI users:
 * - If NO existing water data: fields are HIDDEN (NI users don't pay water charges)
 * - If existing water data (legacy/creditor): fields shown with "(not applicable to Northern Ireland)"
 * 
 * @param schema - The schema to transform (will be mutated)
 * @param isNorthernIreland - Whether to apply NI transformations
 * @param options - Additional options including expense data for water field visibility
 * @returns The transformed schema (same reference as input)
 * 
 * @example
 * ```typescript
 * const schemaClone = JSON.parse(JSON.stringify(originalSchema));
 * applyNISchemaTransformations(schemaClone, isNorthernIreland, {
 *   expenseData: { waterBill: 0, waterWaste: 0 }
 * });
 * ```
 */
export function applyNISchemaTransformations<T extends SchemaConfig>(
  schema: T,
  isNorthernIreland: boolean,
  options: NITransformOptions = {}
): T {
  // Skip if not NI or no schema
  if (!isNorthernIreland || !schema) {
    return schema;
  }

  // Check each water field individually
  const hasWaterBill = hasExistingWaterBillData(options.expenseData);
  const hasWaterWaste = hasExistingWaterWasteData(options.expenseData);

  // Transform each section's fields
  if (schema.sections && Array.isArray(schema.sections)) {
    for (const section of schema.sections) {
      // ========================================
      // Section-level transformations for NI
      // ========================================
      
      // Check if this is the utilities or household section (needs water text removal)
      const isUtilities = isUtilitiesSection(section);
      const isHousehold = isHouseholdSection(section);
      // Sections that need water text removal from FAQs
      const needsWaterTextRemoval = isUtilities || isHousehold;
      
      // 1. Transform sectionHelperText (description at top of page)
      if (typeof section.sectionHelperText === 'string') {
        // Apply council tax → council rates transformation
        section.sectionHelperText = replaceCouncilTaxWithRates(section.sectionHelperText);
        
        // For utilities section: remove "Water" from the list
        // e.g., "Gas, Electricity, Water, Internet" → "Gas, Electricity, Internet"
        if (isUtilities) {
          section.sectionHelperText = removeWaterFromText(section.sectionHelperText);
        }
      }
      
      // 2. Transform section-level helpDialog (main FAQ panel)
      if (section.helpDialog) {
        // Transform description
        if (typeof section.helpDialog.description === 'string') {
          section.helpDialog.description = replaceCouncilTaxWithRates(section.helpDialog.description);
          
          // For utilities section: remove "water" from the description
          // e.g., "gas, electricity, water and other" → "gas, electricity and other"
          if (isUtilities) {
            section.helpDialog.description = removeWaterFromText(section.helpDialog.description);
          }
        }
        
        // Transform FAQs (questions and answers)
        if (section.helpDialog.faqs && Array.isArray(section.helpDialog.faqs)) {
          section.helpDialog.faqs = section.helpDialog.faqs
            // Apply transformations (removeWaterFromText handles "or water" removal)
            .map((faq: FAQ) => {
              let question = replaceCouncilTaxWithRates(faq.question);
              let answer = replaceCouncilTaxWithRates(faq.answer);
              
              // For utilities and household sections: remove "water" mentions from FAQs
              // e.g., "gas, electricity, water and other" → "gas, electricity and other"
              // This handles the "Do I include utilities here?" FAQ in household section
              if (needsWaterTextRemoval) {
                question = removeWaterFromText(question);
                answer = removeWaterFromText(answer);
              }
              
              return { question, answer };
            });
        }
      }
      
      // ========================================
      // Field-level transformations
      // ========================================
      if (!section.fields) continue;

      // Handle water fields for NI users - individual visibility
      section.fields = section.fields.filter((field: FieldConfig) => {
        // Water supply: hide if no water bill data
        if (field.fieldName === 'water') {
          return hasWaterBill; // Show only if water bill data exists
        }
        // Water waste: hide if no water waste data
        if (field.fieldName === 'waterWaste') {
          return hasWaterWaste; // Show only if water waste data exists
        }
        return true;
      });

      // Apply label transform for visible water fields (add "not applicable" note)
      // Only transform fields that have data and are still visible
      const activeWaterTransforms: NILabelTransform[] = [];
      if (hasWaterBill) {
        const waterTransform = NI_WATER_TRANSFORMS.find(t => t.fieldName === 'water');
        if (waterTransform) activeWaterTransforms.push(waterTransform);
      }
      if (hasWaterWaste) {
        const waterWasteTransform = NI_WATER_TRANSFORMS.find(t => t.fieldName === 'waterWaste');
        if (waterWasteTransform) activeWaterTransforms.push(waterWasteTransform);
      }
      if (activeWaterTransforms.length > 0) {
        visitAndTransformFields(section.fields, activeWaterTransforms);
      }

      // Apply other NI transforms (council tax → council rates, savings)
      visitAndTransformFields(section.fields, NI_TRANSFORMS);
    }
  }

  return schema;
}

/**
 * Check if a schema has any NI-affected fields
 * Useful for optimization - skip transformation if no relevant fields
 */
export function schemaHasNIFields(schema: SchemaConfig): boolean {
  const affectedFieldNames = NI_TRANSFORMS.map((t) => t.fieldName);

  const checkFields = (fields: FieldConfig[] | undefined): boolean => {
    if (!fields || !Array.isArray(fields)) {
      return false;
    }

    for (const field of fields) {
      if (field.fieldName && affectedFieldNames.includes(field.fieldName)) {
        return true;
      }
      if (field.children && checkFields(field.children)) {
        return true;
      }
    }
    return false;
  };

  return (schema.sections || []).some((section) => checkFields(section.fields));
}

export default applyNISchemaTransformations;
